import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { flatten, filter as ldFilter, map } from 'lodash-es';
import { filter, switchMap, take } from 'rxjs';

import { AuthService } from '../auth';
import { TrackingService } from '../tracking';

import { AccountTypeEnum } from 'app/graphql/generated/types-user';
import { GAEventEnum, User } from 'app/interfaces';

// gtag.js attaches itself to the global window object with no bundled types
type GaWindow = Window & { gtag?: (...args: unknown[]) => void };

@Injectable()
export class GoogleAnalyticsService {
    private authService = inject(AuthService);
    private trackingService = inject(TrackingService);

    event(eventName: GAEventEnum, params?: object) {
        this.trackingService
            .isTrackingAllowed()
            .pipe(
                take(1),
                filter((allowed) => !!allowed),
                switchMap(() => this.authService.me().pipe(take(1))),
                filter((me): me is User => !!me),
            )
            .subscribe((me) => {
                const eventProperties = this.getAnalyticsEventProperties(me);
                const w = window as GaWindow;
                if (typeof w.gtag === 'function') {
                    w.gtag('event', eventName, {
                        ...eventProperties,
                        ...params,
                    });
                }
            });
    }

    private getAnalyticsEventProperties = (me: User) => {
        const compensationAccounts = ldFilter(me?.activeAccounts, {
            type: AccountTypeEnum.CREDIT,
        });
        const hasCompensation = compensationAccounts.length > 0;
        const compensationLeft = compensationAccounts.map((account) => account.balance)?.reduce((a, b) => a + b, 0);
        const reloadAmount = compensationAccounts
            .map((account) => account?.compensationrule?.automatic_reload_amount)
            ?.reduce((a, b) => a + b, 0);
        const smallestCompensationPeriod = Math.min(
            ...compensationAccounts.map((account) => account?.compensationrule?.compensation_period),
        );
        const taxExemptOnly = compensationAccounts.every((account) => account?.compensationrule?.tax_exception_only);
        const compensatedCategories = flatten(
            map(compensationAccounts, ({ compensationrule }) =>
                map(compensationrule?.allCategories, (category) => category?.name),
            ),
        );
        const uniqueCompensatedCategories = [...new Set(compensatedCategories)];
        const has_insurance = !!me.insurances.find(
            (insurance) => dayjs().isAfter(insurance.startAt) && dayjs().isBefore(insurance.endAt),
        );

        return {
            mobile: false,
            has_compensation: hasCompensation,
            ...(hasCompensation ? { compensation_left: compensationLeft } : {}),
            ...(hasCompensation ? { reload_amount: reloadAmount } : {}),
            ...(hasCompensation ? { compensation_period: smallestCompensationPeriod } : {}),
            ...(hasCompensation ? { only_tax_exempt: taxExemptOnly } : {}),
            ...(hasCompensation ? { compensated_categories: uniqueCompensatedCategories.length } : {}),
            personal_funds: me?.activeAccounts.find((a) => a.type === AccountTypeEnum.DEBIT)?.balance,
            has_insurance,
            age: dayjs().diff(me?.profile?.birth_date, 'years'),
            gender: me?.profile?.gender,
            language_abbr: me?.settings?.language_abbr,
            accepts_email_marketing: me?.settings?.accepts_direct_marketing,
        };
    };
}
